import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import RijekaButton from '../components/RijekaButton';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return dateString;
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateString;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccess = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [bookingDetails, setBookingDetails] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  // Set language immediately from localStorage (for loading screen)
  useEffect(() => {
    const savedLang = localStorage.getItem('booking_language');
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/bookings/checkout-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setBookingDetails(response.data);
          
          // Clean up localStorage after successful booking
          localStorage.removeItem('booking_language');
          
          // Set language based on booking language (fallback)
          const bookingLang = response.data.booking_details?.lang;
          if (bookingLang && bookingLang !== i18n.language) {
            i18n.changeLanguage(bookingLang);
          }
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        
        if (error.response?.status === 409) {
          const detail = error.response.data?.detail;
          
          if (typeof detail === 'object' && detail.error === 'booking_unavailable_after_payment') {
            setStatus('room_unavailable');
            setErrorDetails(detail);
            return;
          }
        }
        
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, navigate, i18n]);

  // Calculate extras total
  const calculateExtrasTotal = () => {
    const extraServices = bookingDetails?.booking_details?.extra_services || [];
    return extraServices.reduce((total, service) => {
      const quantity = service.selected_quantity || 1;
      return total + (service.price_per_unit * quantity);
    }, 0);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
      {/* Unified Header - Logo Only */}
      <Header />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12 flex-1 flex flex-col">
        <div className="mx-auto w-full flex-1 flex flex-col">
          {status === 'checking' && (
            <div className="bg-white shadow-2xl flex-1 flex items-center justify-center rounded-lg" data-testid="checking-status">
              <div className="py-12 sm:py-16 text-center px-4 sm:px-8">
                <div className="inline-block animate-spin h-16 w-16 sm:h-20 sm:w-20 border-4 border-[var(--color-primary)] border-r-transparent rounded-full mx-auto mb-6 sm:mb-8"></div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl mb-3 sm:mb-4 font-bold text-[var(--color-text-primary)]">{t('success.processingPayment', 'Processing Payment')}</h2>
                <p className="text-gray-600 text-sm sm:text-lg">{t('success.pleaseWait', 'Please wait while we confirm your payment...')}</p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-[var(--color-background-light)] flex items-center" data-testid="success-status">
              <div className="w-full">
                <div className="bg-white shadow-2xl mx-auto rounded-lg overflow-hidden" style={{maxWidth: '1400px'}}>
                  {/* Main Responsive Layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    
                    {/* Left Panel - Confirmation */}
                    <div className="p-6 sm:p-10 md:p-14 lg:p-16 flex flex-col justify-between text-white min-h-[300px] sm:min-h-[400px] lg:min-h-[700px]" style={{background: 'linear-gradient(to bottom right, var(--color-primary), var(--color-primary-hover))'}}>
                      <div>
                        <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-white/20 flex items-center justify-center mb-4 sm:mb-6 md:mb-10">
                          <svg className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <h2 className="text-xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-5 leading-tight" data-testid="booking-success-title">{bookingDetails?.pms_error_detail ? t('success.reservationPendingTitle') : t('success.title')}</h2>
                        <p className="text-white/90 text-sm sm:text-base md:text-lg mb-4 sm:mb-8 leading-relaxed">
                          {t('success.confirmationSent')}
                        </p>
                        <div className="space-y-4 sm:space-y-5 md:space-y-6">
                          <div className="flex items-start gap-3 sm:gap-4">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <p className="font-bold text-base sm:text-lg">{t('success.checkEmail', 'Check your email')}</p>
                              <p className="text-sm sm:text-base text-white/80 mt-1">{t('success.checkInInstructions', 'Self check-in instructions sent')}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 sm:gap-4">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="font-bold text-base sm:text-lg">{t('success.paymentConfirmed', 'Payment confirmed')}</p>
                              {!bookingDetails?.pms_error_detail && <p className="text-sm sm:text-base text-white/80 mt-1">{t('success.noFurtherAction', 'No further action needed')}</p>}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="pt-6 sm:pt-8 md:pt-10 border-t border-white/20 mt-6 sm:mt-8 md:mt-0">
                        <p className="text-sm sm:text-base text-white/80">{theme.brand_name || 'RijekaBooking'}</p>
                      </div>
                    </div>

                    {/* Right Panel - Booking Details & Invoice */}
                    <div className="bg-white p-8 sm:p-10 md:p-14 lg:p-16 flex flex-col justify-between">
                      {bookingDetails && (
                        <div className="flex flex-col h-full">
                          {/* Header */}
                          <div className="mb-8">
                            {!bookingDetails?.pms_error_detail && (
                              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                                {t('success.thankYou')}
                              </p>
                            )}
                          </div>

                          {bookingDetails?.pms_error_detail && (
                            <div data-testid="pms-sync-warning" className="mb-8 p-4 border-l-4 border-amber-500 bg-amber-50 rounded">
                              <p className="font-bold text-amber-900 mb-1">{t('success.reservationPendingTitle')}</p>
                              <p className="text-sm text-amber-900/90 leading-relaxed">
                                {t('success.reservationPendingBody')}{' '}
                                <span className="font-mono">{sessionId?.slice(-12)}</span>
                              </p>
                              <p className="text-xs text-amber-900/70 mt-2">
                                {t('success.reservationPendingReason')} {bookingDetails.pms_error_detail}
                              </p>
                            </div>
                          )}

                          {/* Booking Summary */}
                          <div className="space-y-5 mb-8">
                            {/* Dates */}
                            <div className="pb-5 border-b border-gray-200">
                              <p className="text-sm text-gray-500 mb-2 uppercase tracking-wide font-semibold">{t('success.stayDates', 'Stay Dates')}</p>
                              <p className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
                                <span className="whitespace-nowrap">{formatDate(bookingDetails.metadata?.check_in) || 'N/A'}</span>
                                <span className="text-[var(--color-primary)] mx-2">→</span>
                                <br className="sm:hidden" />
                                <span className="whitespace-nowrap">{formatDate(bookingDetails.metadata?.check_out) || 'N/A'}</span>
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {bookingDetails.metadata?.nights || '0'} {parseInt(bookingDetails.metadata?.nights || '0') === 1 ? t('booking.night') : t('booking.nights')}
                              </p>
                            </div>

                            {/* Guests */}
                            <div className="pb-5 border-b border-gray-200">
                              <p className="text-sm text-gray-500 mb-2 uppercase tracking-wide font-semibold">{t('booking.guests')}</p>
                              <p className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
                                {bookingDetails.metadata?.adults || '0'} {t('booking.adultsCount')} 
                                {parseInt(bookingDetails.metadata?.children || '0') > 0 && (
                                  <>, {bookingDetails.metadata?.children} {t('booking.childrenCount')}</>
                                )}
                              </p>
                            </div>

                            {/* Applied Rate */}
                            {bookingDetails.booking_details?.rate_name && (
                              <div className="pb-5 border-b border-gray-200">
                                <p className="text-sm text-gray-500 mb-2 uppercase tracking-wide font-semibold">{t('success.appliedRate', 'Applied Rate')}</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-lg sm:text-xl font-bold text-[var(--color-primary)]">
                                    {bookingDetails.booking_details.rate_name}
                                  </p>
                                  {bookingDetails.booking_details.rate_discount > 0 && (
                                    <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded font-semibold">
                                      -{bookingDetails.booking_details.rate_discount}%
                                    </span>
                                  )}
                                  {bookingDetails.booking_details.rate_surcharge > 0 && (
                                    <span className="bg-pink-500 text-white text-xs px-2 py-0.5 rounded font-semibold">
                                      +€{bookingDetails.booking_details.rate_surcharge}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Invoice / Cost Breakdown */}
                            <div className="pb-5 border-b border-gray-200">
                              <p className="text-sm text-gray-500 mb-3 uppercase tracking-wide font-semibold">{t('success.costBreakdown', 'Cost Breakdown')}</p>
                              
                              {/* Base Room Price */}
                              <div className="flex justify-between items-center py-2">
                                <span className="text-gray-700">
                                  {t('success.roomPrice', 'Room')} ({bookingDetails.metadata?.nights || '0'} {parseInt(bookingDetails.metadata?.nights || '0') === 1 ? t('booking.night') : t('booking.nights')} × €{bookingDetails.metadata?.price_per_night || '0'})
                                </span>
                                <span className="font-semibold text-[var(--color-text-primary)]">
                                  €{(parseFloat(bookingDetails.metadata?.price_per_night || 0) * parseInt(bookingDetails.metadata?.nights || 0)).toFixed(2)}
                                </span>
                              </div>

                              {/* Extra Guest Charge */}
                              {bookingDetails.booking_details?.extra_guest_total > 0 && (
                                <div className="flex justify-between items-center py-2 bg-amber-50 -mx-3 px-3 rounded">
                                  <span className="text-amber-700">
                                    {t('success.extraGuest', 'Dodatni gost')} ({bookingDetails.booking_details.extra_guests_count} × €{bookingDetails.booking_details.extra_guest_price} × {bookingDetails.metadata?.nights || '0'} {parseInt(bookingDetails.metadata?.nights || '0') === 1 ? t('booking.night') : t('booking.nights')})
                                  </span>
                                  <span className="font-semibold text-amber-700">
                                    €{parseFloat(bookingDetails.booking_details.extra_guest_total).toFixed(2)}
                                  </span>
                                </div>
                              )}

                              {/* Extra Services */}
                              {bookingDetails.booking_details?.extra_services && bookingDetails.booking_details.extra_services.length > 0 && (
                                <>
                                  <div className="border-t border-gray-100 my-2"></div>
                                  <p className="text-sm text-gray-500 mb-2 font-medium">{t('booking.extraServicesTitle', 'Extra Services')}:</p>
                                  {bookingDetails.booking_details.extra_services.map((service, index) => (
                                    <div key={index} className="flex justify-between items-center py-1 pl-4">
                                      <span className="text-gray-600 text-sm">
                                        {service.name}
                                        {service.type === 'quantity' && service.selected_quantity > 1 && (
                                          <span className="text-gray-500"> × {service.selected_quantity}</span>
                                        )}
                                      </span>
                                      <span className="font-medium text-[var(--color-text-primary)]">
                                        €{(service.price_per_unit * (service.selected_quantity || 1)).toFixed(2)}
                                      </span>
                                    </div>
                                  ))}
                                  <div className="flex justify-between items-center py-2 mt-2 bg-gray-50 px-3 rounded">
                                    <span className="text-gray-700 font-medium">{t('success.extrasSubtotal', 'Extras Subtotal')}</span>
                                    <span className="font-semibold text-[var(--color-primary)]">€{calculateExtrasTotal().toFixed(2)}</span>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Total */}
                            <div className="bg-[var(--color-dark)] text-white p-4 rounded-lg">
                              <div className="flex justify-between items-center">
                                <p className="text-lg font-semibold">{t('success.totalPaid', 'Total Paid')}</p>
                                <p className="text-3xl sm:text-4xl font-bold text-[var(--color-primary)]">€{bookingDetails.amount_total.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <RijekaButton 
                              onClick={() => navigate('/')}
                              className="py-4 text-base w-full"
                              data-testid="return-home-button"
                            >
                              {t('success.backToHome')}
                            </RijekaButton>
                            <RijekaButton 
                              variant="outline"
                              onClick={() => navigate('/')}
                              className="py-4 text-base w-full"
                            >
                              {t('success.viewProperty', 'View Property')}
                            </RijekaButton>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}

          {status === 'room_unavailable' && (
            <div className="bg-white shadow-2xl" data-testid="room-unavailable-status">
              <div className="bg-yellow-600 p-8 text-center text-white">
                <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-white">
                  <svg className="w-16 h-16 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-5xl font-bold">{t('success.overbookingPrevented', 'OVERBOOKING PREVENTED')}</h2>
              </div>
              <div className="p-10 space-y-8">
                <div className="bg-red-50 border-2 border-red-300 p-6">
                  <h3 className="text-lg font-bold mb-4 text-red-900 uppercase tracking-wide flex items-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('success.whatHappened', 'What Happened?')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    {errorDetails?.message || t('success.roomBookedByAnother', "We're very sorry, but this room was booked by another guest while you were completing your payment.")}
                  </p>
                  <p className="text-gray-700 leading-relaxed font-semibold">
                    {t('success.autoAborted', 'Our system automatically detected that completing your booking would have resulted in an overbooking situation, so the reservation was automatically aborted to prevent a double booking.')}
                  </p>
                </div>

                <div className="bg-green-50 border-2 border-green-300 p-6">
                  <h3 className="text-lg font-bold mb-4 text-green-900 uppercase tracking-wide">{t('success.yourRefund', 'Your Payment & Refund')}</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {errorDetails?.refund_info || t('success.refundInfo')}
                  </p>
                  <div className="flex items-start gap-3 mt-4">
                    <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-semibold text-green-900">{t('success.fullRefund', 'You will receive a full refund - no charges will remain on your card.')}</p>
                  </div>
                </div>

                <div className="text-center pt-6 space-y-4">
                  <p className="text-gray-600">
                    {t('success.apologize', 'We apologize for the inconvenience and appreciate your understanding.')}
                  </p>
                  <RijekaButton 
                    onClick={() => navigate('/')}
                    data-testid="return-home-button"
                    className="px-12 py-6 text-lg font-bold"
                  >
                    {t('success.searchOther', 'SEARCH FOR OTHER PROPERTIES')}
                  </RijekaButton>
                </div>
              </div>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <Card data-testid="error-status">
              <CardHeader className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-red-100 rounded-full">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <CardTitle className="text-4xl" style={{fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)'}}>{t('success.bookingError')}</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <p className="text-stone-600">
                  {status === 'expired' && t('success.sessionExpired', 'Your payment session has expired. If payment was completed, you will receive a confirmation email shortly.')}
                  {status === 'timeout' && t('success.couldntConfirm', "We couldn't confirm your booking status. If your payment was processed, you will receive a confirmation email within a few minutes.")}
                  {status === 'error' && t('success.verifyError', "We couldn't verify your booking status at this time. If your payment was completed, you will receive a confirmation email.")}
                </p>
                <div className="flex gap-4 justify-center">
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/')}
                    data-testid="return-home-button"
                  >
                    {t('success.backToHome')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BookingSuccess;
